import React, { useEffect, useMemo, useState } from "react";
import { Loader2, MessageSquare, X, Send } from "lucide-react";
import { Button } from "../ui/button";

const formatE164Preview = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim();
  if (s.startsWith("+")) return "+" + s.slice(1).replace(/\D/g, "");
  const digits = s.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return digits ? `+${digits}` : "";
};

const SendSmsModal = ({ open, onClose, doc, customer, businessName, paymentLink, onSubmit, submitting, isDark }) => {
  const isEstimate = doc?.document_type === "estimate";
  const docLabel = isEstimate ? "Estimate" : "Invoice";

  const [phone, setPhone] = useState("");

  useEffect(() => {
    if (open) {
      setPhone(customer?.phone || doc?.customer_phone || "");
    }
  }, [open, customer?.phone, doc?.customer_phone]);

  const previewLink = isEstimate
    ? `…/estimate/${(doc?.public_token || "<token>").slice(0, 12)}…`
    : `…/pay/${(doc?.public_token || "<token>").slice(0, 12)}…`;

  const previewMessage = useMemo(() => {
    const intro = businessName ? `From ${businessName}: ` : "";
    const num = doc?.invoice_number || (doc?.invoice_id || "").slice(0, 8);
    if (paymentLink?.url) {
      const amt = (paymentLink.amount || doc?.total || 0).toFixed(2);
      const shortLink = `${paymentLink.url.slice(0, 42)}…`;
      return `${intro}Payment request for ${docLabel.toLowerCase()} ${num}: $${amt}. Pay securely: ${shortLink}\nReply STOP to opt out.`;
    }
    const total = (doc?.total ?? 0).toFixed(2);
    return `${intro}Your ${docLabel.toLowerCase()} ${num} is ready. Total: $${total}. View: ${previewLink}\nReply STOP to opt out.`;
  }, [businessName, doc, docLabel, previewLink, paymentLink]);

  if (!open || !doc) return null;

  const normalized = formatE164Preview(phone);
  const valid = normalized.length >= 8;

  const handleSubmit = () => {
    if (!valid) return;
    onSubmit({ phone_number: phone });
  };

  const overlay = "fixed inset-0 bg-black/40 dark:bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-3";
  const card = "relative rounded-2xl max-w-md w-full bg-background border border-border shadow-2xl overflow-hidden";

  return (
    <div className={overlay} onClick={onClose} data-testid="send-sms-modal">
      <div className={card} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 flex items-center justify-between border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-emerald-500" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">{paymentLink?.url ? "Send Payment Link via SMS" : `Send ${docLabel} via SMS`}</h3>
              <p className="text-[11px] text-muted-foreground">{doc?.invoice_number} · {customer?.name || doc?.customer_name || ""}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" data-testid="close-sms-modal">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] text-muted-foreground uppercase tracking-wider font-medium block mb-1.5">
              Customer Phone
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 555 123 4567"
              className="w-full h-9 px-3 text-sm rounded-lg bg-muted border border-border focus:outline-none focus:border-primary/40 text-foreground"
              data-testid="sms-phone-input"
            />
            {phone && !valid && (
              <p className="text-[11px] text-red-500 mt-1">Enter a valid phone number</p>
            )}
            {valid && (
              <p className="text-[11px] text-muted-foreground mt-1">Will send to <span className="font-mono text-foreground">{normalized}</span></p>
            )}
          </div>

          <div>
            <label className="text-[11px] text-muted-foreground uppercase tracking-wider font-medium block mb-1.5">
              Preview
            </label>
            <div className="rounded-lg border border-border bg-muted/40 p-3">
              <p className="text-xs text-foreground whitespace-pre-wrap leading-relaxed" data-testid="sms-preview">
                {previewMessage}
              </p>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5">
              The actual link will be the full URL. STOP-replies block future SMS to this number.
            </p>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-border bg-muted/20 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} className="text-muted-foreground">
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!valid || submitting}
            onClick={handleSubmit}
            className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5"
            data-testid="confirm-send-sms"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Send SMS
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SendSmsModal;
